import React, {forwardRef, KeyboardEventHandler, useCallback, useImperativeHandle, useMemo, useRef, useState} from 'react'

import EntitySelectorSearchInput from "./search-input"
import EntitySelectorSearchRow from "./search-row"

type Props = {
    placeholder?        : string
    searchDebounceTime? : number    // 0 on the user tab: it filters an in-memory list

    onKeyDown           : KeyboardEventHandler
    onSearchChanged     : {(value: string): void}
}

const debug = false
// const debug = "EntitySelectorSearch"

// The one search line, for every tab. Each tab used to carry its own copy of
// this plus the Recent / Browse buttons; those are sub-tabs now, and what was
// left was the same twenty lines five times over.
//
// The debounced value goes to the tab, the raw one stays here -- typing must not
// wait on the network to show up in the field.
const EntitySelectorSearch = forwardRef((props: Props, ref) => {
    debug && console.log(debug, 'render')

    const [search, setSearch]   = useState('')
    const inputRef              = useRef<any>()

    const delayedSearch = useMemo(()=>_.debounce((search: string)=>{
        debug && console.log(debug, 'delayedSearch', search)
        props.onSearchChanged(search)
    }, props.searchDebounceTime ?? 500), [])

    const onSearchChange = useCallback((value: string)=>{
        debug && console.log(debug, 'onSearchChange', value)
        setSearch(value)
        delayedSearch(value)
    }, [])

    const onSearchCancel = useCallback(()=>{
        setSearch('')
        delayedSearch('')
    }, [])

    // What a sub-tab switch does: the old query means nothing in the new list,
    // and the caret belongs back in the field.
    const reset = useCallback(()=>{
        onSearchCancel()
        inputRef.current?.focus()
    }, [])

    useImperativeHandle(ref, () => ({
        reset,
    }))

    return (
        <EntitySelectorSearchRow>
            <EntitySelectorSearchInput
                ref             = {inputRef}
                search          = {search}
                placeholder     = {props.placeholder || 'Search...'}
                onKeyDown       = {props.onKeyDown}
                onSearchChange  = {onSearchChange}
                onSearchCancel  = {onSearchCancel}
            />
        </EntitySelectorSearchRow>
    )
})

export default EntitySelectorSearch
